import { useState } from "react";
import { Check, Copy, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Attribution block for our open data. Makes it frictionless for bloggers,
 * course creators and LLM crawlers to cite the source correctly — a citable
 * asset is the only backlink source we control.
 */
export default function CiteThisData({
  citationText,
  citationHtml,
  note,
}: {
  citationText: string;
  citationHtml: string;
  note?: string;
}) {
  const [copied, setCopied] = useState<"text" | "html" | null>(null);

  const copy = async (kind: "text" | "html", value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /* clipboard unavailable — the raw text stays selectable below */
    }
  };

  return (
    <section
      aria-labelledby="cite-this-data"
      className="glass-3d rounded-2xl p-6 border border-primary/20"
    >
      <h2
        id="cite-this-data"
        className="font-display text-xl font-bold flex items-center gap-2"
      >
        <Quote className="w-4 h-4 text-primary" aria-hidden="true" />
        Cite this data
      </h2>
      <p className="text-sm text-muted-foreground mt-2">
        {note ??
          "Free to reuse in articles, videos, courses and research with attribution and a link back."}
      </p>

      <div className="mt-4 space-y-4">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">
            Plain citation
          </p>
          <p className="text-sm bg-muted/30 rounded-xl p-3 font-mono leading-relaxed break-words">
            {citationText}
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2 rounded-full"
            onClick={() => copy("text", citationText)}
          >
            {copied === "text" ? (
              <Check className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
            ) : (
              <Copy className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
            )}
            {copied === "text" ? "Copied" : "Copy citation"}
          </Button>
        </div>

        <div>
          <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">
            HTML for your post
          </p>
          <p className="text-xs bg-muted/30 rounded-xl p-3 font-mono leading-relaxed break-all text-muted-foreground">
            {citationHtml}
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2 rounded-full"
            onClick={() => copy("html", citationHtml)}
          >
            {copied === "html" ? (
              <Check className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
            ) : (
              <Copy className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
            )}
            {copied === "html" ? "Copied" : "Copy HTML"}
          </Button>
        </div>
      </div>
    </section>
  );
}
